/**
 * @file sheets/bottom-sheet/use-bottom-sheet-scaffold.ts
 *
 * Core logic for `BottomSheetScaffold` — the standard (non-modal, persistent)
 * bottom sheet layout component.
 *
 * Mirrors Android's `BottomSheetScaffoldState` / `BottomSheetState`:
 * - 2-state machine: `partiallyExpanded` ↔ `expanded` (no `hidden` state)
 * - Sheet is ALWAYS visible at minimum `peekHeight`
 * - Velocity-based fling + nearest-snap logic on drag-end
 * - BoundaryDampeningZone prevents over-flinging
 *
 * @see https://m3.material.io/components/sheets-bottom/overview#standard
 */

import type { PanInfo } from "motion/react";
import { animate, useMotionValue } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SheetValue } from "../shared/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseBottomSheetScaffoldOptions {
	/** Visible height when partially collapsed. Default: 56px. */
	peekHeight?: number;
	/** Whether swipe gestures are enabled. Default: true. */
	swipeEnabled?: boolean;
	/** Called when the sheet state changes. */
	onStateChange?: (
		state: Extract<SheetValue, "partiallyExpanded" | "expanded">,
	) => void;
	/** Initial state. Default: "partiallyExpanded". */
	initialState?: Extract<SheetValue, "partiallyExpanded" | "expanded">;
}

export interface UseBottomSheetScaffoldReturn {
	/** Current discrete sheet state. */
	sheetState: Extract<SheetValue, "partiallyExpanded" | "expanded">;
	/** Y MotionValue of the sheet panel (absolute from top of scaffold container). */
	y: ReturnType<typeof useMotionValue<number>>;
	/** Expand to full height. */
	expand: () => void;
	/** Collapse to peek height. */
	partialExpand: () => void;
	/** Toggle between partiallyExpanded and expanded. */
	toggle: () => void;
	/** Whether drag is allowed. */
	isDragEnabled: boolean;
	/** Drag constraints for m.div. */
	dragConstraints: { top: number; bottom: number };
	/** Drag-end handler — snaps to closest anchor. */
	handleDragEnd: (
		e: MouseEvent | TouchEvent | PointerEvent,
		info: PanInfo,
	) => void;
	/** Scroll handler for nested content. */
	handleContentScroll: (e: React.UIEvent<HTMLElement>) => void;
	/** Height of the content area above the sheet peek. */
	contentPaddingBottom: string;
	/** Callback ref for the scaffold's container element. */
	containerRef: React.RefCallback<HTMLDivElement | null>;
}

const DISMISS_VELOCITY_THRESHOLD = 500;
const BOUNDARY_DAMPENING_ZONE = 80;
const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBottomSheetScaffold({
	peekHeight = 56,
	swipeEnabled = true,
	onStateChange,
	initialState = "partiallyExpanded",
}: UseBottomSheetScaffoldOptions = {}): UseBottomSheetScaffoldReturn {
	const [containerHeight, setContainerHeight] = useState(
		typeof window !== "undefined" ? window.innerHeight : 800,
	);
	const containerNodeRef = useRef<HTMLDivElement | null>(null);

	const containerRef = useCallback((node: HTMLDivElement | null) => {
		containerNodeRef.current = node;
		if (node) {
			setContainerHeight(node.offsetHeight || window.innerHeight);
		}
	}, []) as React.RefCallback<HTMLDivElement | null>;

	// Measure container height on resize
	useEffect(() => {
		const node = containerNodeRef.current;
		if (!node) return;
		const observer = new ResizeObserver(() => {
			setContainerHeight(node.offsetHeight);
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	// Sheet Y positions (absolute from top of container)
	const expandedY = 0; // Sheet top at container top
	const partialY = containerHeight - peekHeight;

	const y = useMotionValue(initialState === "expanded" ? expandedY : partialY);

	const [sheetState, setSheetState] =
		useState<Extract<SheetValue, "partiallyExpanded" | "expanded">>(
			initialState,
		);
	const [isScrollDisablingDrag, setIsScrollDisablingDrag] = useState(false);

	const isDragEnabled = swipeEnabled && !isScrollDisablingDrag;

	const dragConstraints = useMemo(
		() => ({ top: 0, bottom: partialY }),
		[partialY],
	);

	// Update y when container height changes (partialY already derives from containerHeight)
	useEffect(() => {
		const targetY = sheetState === "expanded" ? 0 : partialY;
		void animate(y, targetY, SPRING);
	}, [partialY, sheetState, y]);

	const expand = useCallback(() => {
		setSheetState("expanded");
		void animate(y, 0, SPRING);
		onStateChange?.("expanded");
	}, [y, onStateChange]);

	const partialExpand = useCallback(() => {
		setSheetState("partiallyExpanded");
		void animate(y, partialY, SPRING);
		onStateChange?.("partiallyExpanded");
	}, [y, partialY, onStateChange]);

	const toggle = useCallback(() => {
		if (sheetState === "expanded") partialExpand();
		else expand();
	}, [sheetState, expand, partialExpand]);

	const handleDragEnd = useCallback(
		(_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
			const rawY = y.get();
			let velocity = info.velocity.y;

			// Dampening near the floor (partialY)
			if (velocity > 0) {
				const distanceToFloor = Math.max(0, partialY - rawY);
				if (distanceToFloor < BOUNDARY_DAMPENING_ZONE) {
					const factor = distanceToFloor / BOUNDARY_DAMPENING_ZONE;
					velocity *= factor * factor;
				}
			}

			// Velocity-based fling
			if (velocity > DISMISS_VELOCITY_THRESHOLD) {
				partialExpand();
				return;
			}
			if (velocity < -DISMISS_VELOCITY_THRESHOLD) {
				expand();
				return;
			}

			// Snap to nearest
			const midpoint = partialY / 2;
			if (rawY < midpoint) expand();
			else partialExpand();
		},
		[y, partialY, expand, partialExpand],
	);

	const handleContentScroll = useCallback(
		(e: React.UIEvent<HTMLElement>) => {
			if (sheetState !== "expanded") {
				e.currentTarget.scrollTop = 0;
				setIsScrollDisablingDrag(false);
				return;
			}
			setIsScrollDisablingDrag(e.currentTarget.scrollTop > 0);
		},
		[sheetState],
	);

	const contentPaddingBottom = `${peekHeight}px`;

	return {
		sheetState,
		y,
		expand,
		partialExpand,
		toggle,
		isDragEnabled,
		dragConstraints,
		handleDragEnd,
		handleContentScroll,
		contentPaddingBottom,
		containerRef,
	};
}
